import type { AppUpdateArtifact, DeviceModel } from '../../services/admin';

// Presentation helpers for the APK Repository's artifact evidence (Phase C3.5). Everything here
// only DESCRIBES what the server returned - trust decisions (validation, model policy, signer
// policy) are made by the backend, never in the browser.

export type ArtifactStatusKind = 'VALIDATED' | 'LEGACY' | 'PENDING_INSPECTION' | 'UNKNOWN';

export interface ArtifactStatusView {
  kind: ArtifactStatusKind;
  label: string;
  description: string;
}

export function artifactStatusView(status: string | null | undefined): ArtifactStatusView {
  switch (status) {
    case 'VALIDATED':
      return {
        kind: 'VALIDATED',
        label: 'Validated',
        description: 'The full build identity passed strict validation and model policy when it was uploaded.',
      };
    case 'LEGACY':
      return {
        kind: 'LEGACY',
        label: 'Legacy (inspected)',
        description:
          'Historical release. Its signature, package, version, hash and size were checked retrospectively; it predates the build-target marker contract.',
      };
    case 'PENDING_INSPECTION':
      return {
        kind: 'PENDING_INSPECTION',
        label: 'Pending inspection',
        description: 'Trust classification has not been completed for this release yet.',
      };
    default:
      return {
        kind: 'UNKNOWN',
        label: status ? `Unknown state (${status})` : 'Unknown state',
        description: 'The server reported no recognised artifact state for this release.',
      };
  }
}

// A canonical model has the C1 manufacturer/model identity; only those can receive uploads. The
// backend enforces the same rule (MODEL_NOT_CANONICAL) - this filter is only a convenience.
export function isCanonicalModel(m: Pick<DeviceModel, 'manufacturer_code' | 'model_code'>): boolean {
  return !!m.manufacturer_code?.trim() && !!m.model_code?.trim();
}

export function canonicalUploadModels<T extends DeviceModel>(models: T[]): T[] {
  return models.filter(isCanonicalModel).sort((a, b) => modelOptionLabel(a).localeCompare(modelOptionLabel(b)));
}

export function modelOptionLabel(m: DeviceModel): string {
  return `${m.display_name || m.model_name} (${m.manufacturer_code} / ${m.model_code})`;
}

// "c8a2e9bccf59…11f51192ab8" -> "c8a2e9bc…92ab8". The full value stays available in the detail view.
export function shortFingerprint(fingerprint: string): string {
  const fp = fingerprint.trim().toLowerCase();
  return fp.length > 16 ? `${fp.slice(0, 8)}…${fp.slice(-5)}` : fp;
}

// Current signer policy for the release's model, as derived by the server. Compatibility policy
// only (which certificate a model's platform accepts) - it says nothing about who built the APK.
export function signerPolicyLabel(status: string | null | undefined): string | null {
  switch (status) {
    case 'ACTIVE':
      return 'Allowed compatibility signer';
    case 'RETIRED':
      return 'Retired compatibility signer';
    case 'NOT_ALLOWED':
      return 'Not an allowed compatibility signer for this model';
    case null:
    case undefined:
      return null;
    default:
      return `Unknown policy state (${status})`;
  }
}

export type BuildTargetView =
  | { kind: 'declared'; target: string; schema: string | null }
  | { kind: 'absent' | 'pending' | 'unavailable'; text: string };

// The artifact-DECLARED target, never inferred from the catalog association.
export function buildTargetView(artifact: AppUpdateArtifact | undefined): BuildTargetView {
  const status = artifact?.status;
  if (artifact && status !== 'LEGACY' && status !== 'PENDING_INSPECTION' && artifact.target_manufacturer_code && artifact.target_model_code) {
    return {
      kind: 'declared',
      target: `${artifact.target_manufacturer_code} / ${artifact.target_model_code}`,
      schema: artifact.target_schema,
    };
  }
  if (status === 'LEGACY') return { kind: 'absent', text: 'Not present — predates marker contract' };
  if (status === 'PENDING_INSPECTION') return { kind: 'pending', text: 'Not yet available — release not inspected' };
  return { kind: 'unavailable', text: 'Not available' };
}

// C3.4 errors are plain text "CODE: message". Anything else (proxy HTML pages, empty bodies) has
// no code.
export function parseServerError(body: string): { code: string | null; message: string } {
  const text = body.trim();
  const m = /^([A-Z][A-Z0-9_]*): ([\s\S]*)$/.exec(text);
  if (m) return { code: m[1], message: m[2].trim() };
  if (!text || text.startsWith('<')) return { code: null, message: '' };
  return { code: null, message: text.length > 300 ? `${text.slice(0, 300)}…` : text };
}

const NOTHING_PUBLISHED = 'Nothing was published.';

const UPLOAD_ERRORS: Record<string, string> = {
  INVALID_REQUEST: 'The upload request was not accepted.',
  MODEL_NOT_FOUND: 'The selected device model no longer exists. Reload the page and select it again.',
  MODEL_NOT_CANONICAL: 'The selected device model has no canonical manufacturer/model identity, so it cannot receive uploads.',
  UPLOAD_INCOMPLETE: 'The upload did not complete. Try again.',
  UPLOAD_TOO_LARGE: "The APK exceeds the server's upload size limit.",
  APK_INVALID: 'The APK failed strict validation (signature, structure or build-target marker).',
  PACKAGE_NOT_ALLOWED: 'The APK is not the PTT Radio application package.',
  VERSION_CODE_OUT_OF_RANGE: "The APK's versionCode is outside the accepted range (1–2147483647).",
  VERSION_NAME_INVALID: "The APK's versionName is missing or invalid.",
  BUILD_TARGET_SCHEMA_UNSUPPORTED: "The APK's build-target marker uses an unsupported schema.",
  BUILD_TARGET_MISMATCH: 'The APK was built for a different device model than the one selected.',
  SIGNER_NOT_ALLOWED: "The APK's signing certificate is not an allowed compatibility signer for this model.",
  VERSION_ALREADY_PUBLISHED: 'A release with this versionCode already exists for this model.',
  PUBLICATION_CONFLICT: 'Another change to this model happened at the same time. Reload the page and try again.',
  VALIDATOR_BUSY: 'The APK validator is busy. Try again shortly.',
  VALIDATOR_UNAVAILABLE: 'The APK validator is unavailable.',
  VALIDATOR_TIMEOUT: 'APK validation timed out.',
  INGESTION_NOT_CONFIGURED: 'Trusted APK ingestion is not configured on this server.',
  PUBLICATION_FAILED: 'The server could not publish the release.',
};

// One user-facing sentence per failed upload. A coded error came from the backend, whose contract
// is "one VALIDATED release or nothing", so nothing was published. An uncoded 5xx or a network
// failure may come from a proxy that gave up while the backend was still working - the release
// may then exist, so the admin is told to check the list before retrying.
export function describeUploadError(status: number | null, body: string): string {
  const { code, message } = parseServerError(body);
  const detail = code ? ` (${code}${message ? `: ${message}` : ''})` : '';
  if (code) {
    const known = UPLOAD_ERRORS[code] ?? (code.startsWith('STAGING_') ? 'The server could not stage the upload.' : null);
    return `${known ?? 'The server rejected the upload.'} ${NOTHING_PUBLISHED}${detail}`;
  }
  if (status === 413) return 'The upload was rejected as too large by the server or proxy (HTTP 413). Nothing was published.';
  if (status === null || status >= 500) {
    const what = status === null ? 'The connection failed before the server answered' : `The server or proxy did not complete the request (HTTP ${status})`;
    return `${what}. The APK may still have been published - check the release list before retrying.${message ? ` ${message}` : ''}`;
  }
  return `The upload failed (HTTP ${status}).${message ? ` ${message}` : ''}`;
}

// Runs at most one task at a time; a call while one is in flight is dropped (returns undefined).
// Guards the upload against double submission even before React re-renders the disabled button.
export function createExclusiveRunner() {
  let running = false;
  return async <T>(task: () => Promise<T>): Promise<T | undefined> => {
    if (running) return undefined;
    running = true;
    try {
      return await task();
    } finally {
      running = false;
    }
  };
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatTimestamp(value: string): string {
  return new Intl.DateTimeFormat('da-DK', { dateStyle: 'short', timeStyle: 'medium', hour12: false }).format(new Date(value));
}
